import {
  type DatabaseContainerType,
  getDatabaseContainerType,
  useAnalytics as getAnalyticsClient
} from '@databases/analytics';
import { getSelectionsAnalyticsAttributes } from '@databases/analytics';
import { collabStateManager } from '@databases/collab-state-manager';
import { getActivationId } from '@databases/database-state';
import {
  type Database,
  type DatabaseFieldType,
  DatabaseFieldType as DatabaseFieldTypeValue,
  isDatabaseTextValue
} from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  addChangeToChangeset,
  type DatabaseChange,
  type DatabaseChangeset
} from '@databases/utils/database/changesets';
import {
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
  getDatabaseValue,
  isBrokenDatabase
} from '@databases/utils/database/utils';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import { deserializeError } from '@databases/utils/utils';
import { debounce } from 'lodash-es';
import { type ThunkAction } from 'redux-thunk';

import { type DefaultRootState } from '../redux.ts';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, never>;

const sendDatabaseCellContentHasBeenModifiedEventDebounced = debounce(
  async (
    activationId: string | undefined,
    contentId: string,
    containerType: DatabaseContainerType,
    fieldType: DatabaseFieldType
  ) => {
    const client = getAnalyticsClient();
    if (client !== undefined) {
      await client.sendDatabaseCellContentHasBeenModifiedEvent({
        activationId: activationId,
        contentId: contentId,
        databaseContainer: containerType,
        fieldType,
        ...getSelectionsAnalyticsAttributes(contentId)
      });
    }
  },
  1000
);

const pendingAiFieldManualEditEvents = new Map<string, ReturnType<typeof debounce>>();

const sendAiFieldValueManuallyEditedEventDebounced = (
  contentId: string,
  fieldId: string,
  entryId: string,
  overrideTarget: 'aiGeneratedValue' | 'emptyCell' | 'manualValue'
) => {
  const key = `${contentId}:${fieldId}:${entryId}`;
  let pendingEvent = pendingAiFieldManualEditEvents.get(key);

  if (!pendingEvent) {
    pendingEvent = debounce(async () => {
      pendingAiFieldManualEditEvents.delete(key);
      const client = getAnalyticsClient();
      await client?.sendAiFieldValueManuallyEditedEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        fieldId,
        entryId,
        aiFieldType: 'summary',
        overrideTarget
      });
    }, 1000);
    pendingAiFieldManualEditEvents.set(key, pendingEvent);
  }

  pendingEvent();
};

const applyChangetoCollabDatabase =
  (database: Database, changeset: DatabaseChangeset): Dispatcher<void> =>
  (dispatch, getState) => {
    const collabManager = collabStateManager.getCollabManager(database.contentId);
    if (collabManager) {
      collabManager.applyChangeset(changeset, getState());
    }
  };

export const addChangeToDraft =
  (contentId: string, change: DatabaseChange): Dispatcher<void> =>
  (dispatch, getState) => {
    const database = selectDatabaseByContentIdOptional(getState(), { contentId });
    if (!database) {
      throw new Error(`Database ${contentId} not found.`);
    } else if (isBrokenDatabase(database)) {
      throw deserializeError(database.error);
    }

    if (change.type === DatabaseChangeType.SET_VALUE && change.change?.type) {
      sendDatabaseCellContentHasBeenModifiedEventDebounced(
        getActivationId(),
        contentId,
        getDatabaseContainerType(),
        change.change.type
      );

      const field = getDatabaseFieldRequired(database, change.fieldId);
      if (field.type === DatabaseFieldTypeValue.AI_SUMMARY) {
        const entry = getDatabaseEntryRequired(database, change.entryId);
        const previousDatabaseValue = getDatabaseValue(database, field, entry);
        const previousValue = previousDatabaseValue.value;
        const isAiGeneratedValue =
          previousDatabaseValue.type === DatabaseFieldTypeValue.AI_SUMMARY &&
          previousDatabaseValue.value.metadata?.lastUpdatedBy === 'ai';
        const overrideTarget = isAiGeneratedValue
          ? 'aiGeneratedValue'
          : !isDatabaseTextValue(previousValue) || previousValue.plain.trim().length === 0
            ? 'emptyCell'
            : 'manualValue';
        sendAiFieldValueManuallyEditedEventDebounced(
          contentId,
          change.fieldId,
          change.entryId,
          overrideTarget
        );
      }
    }

    const changeset = addChangeToChangeset([], change);
    dispatch(applyChangetoCollabDatabase(database, changeset));
  };

export const setDraftedChangeset =
  (contentId: string, changeset: DatabaseChangeset): Dispatcher<void> =>
  (dispatch, getState) => {
    const database = selectDatabaseByContentIdOptional(getState(), { contentId });
    if (!database) {
      throw new Error(`Database ${contentId} not found.`);
    } else if (isBrokenDatabase(database)) {
      throw deserializeError(database.error);
    }

    dispatch(applyChangetoCollabDatabase(database, changeset));
  };
